import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  Stack,
  Button,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { FixedSizeList as List } from 'react-window';
import { TreatmentService } from '@/services/treatmentService';
import { TreatmentRecord } from '@/types/treatment';
import TimelineCard from './TimelineCard';
import TimelineEmpty from './TimelineEmpty';
import { format, isWithinInterval, subMonths } from 'date-fns';
import { ja } from 'date-fns/locale';

interface TimelineViewProps {
  customerId: number;
  onSelectRecord?: (record: TreatmentRecord) => void;
}

interface TimelineFilter {
  period: 'all' | '1month' | '3months' | '6months' | '1year' | 'custom';
  menu: string;
  staff: string;
  keyword: string;
  startDate?: Date;
  endDate?: Date;
}

const TimelineView: React.FC<TimelineViewProps> = ({ customerId, onSelectRecord }) => {
  const [treatments, setTreatments] = useState<TreatmentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<TimelineFilter>({
    period: 'all',
    menu: '',
    staff: '',
    keyword: ''
  });
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  
  // Load treatment records
  useEffect(() => {
    loadTreatments();
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const loadTreatments = async () => {
    setLoading(true);
    console.log(`Loading treatments for customer ${customerId}...`);
    
    try {
      const records = await TreatmentService.getTreatmentRecords(customerId);
      console.log(`Loaded ${records.length} treatment records for customer ${customerId}`);
      
      // デバッグ情報を出力
      if (import.meta.env.DEV) {
        console.log('Treatment records:', records);
        
        // データ整合性の確認
        const integrity = (window as any).InitialDataService?.checkDataIntegrity?.();
        if (integrity) {
          console.log('Data integrity check:', integrity);
        }
      }
      
      setTreatments(records);
    } catch (error) {
      console.error('Error loading treatments:', error);
      console.error('Error details:', {
        customerId,
        localStorage: localStorage.getItem(`treatments_${customerId}`),
        error: error instanceof Error ? error.message : error
      });
      setTreatments([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Get unique values for filters
  const { menus, staffList } = useMemo(() => {
    const menuSet = new Set<string>();
    const staffSet = new Set<string>();
    
    treatments.forEach(t => {
      if (t.treatmentMenu) menuSet.add(t.treatmentMenu);
      if (t.staffName) staffSet.add(t.staffName);
    });
    
    return {
      menus: Array.from(menuSet).sort(),
      staffList: Array.from(staffSet).sort()
    };
  }, [treatments]);
  
  // Filter treatments
  const filteredTreatments = useMemo(() => {
    let filtered = [...treatments];
    
    // Period filter
    if (filter.period !== 'all') {
      const now = new Date();
      let startDate: Date;
      
      switch (filter.period) {
        case '1month':
          startDate = subMonths(now, 1);
          break;
        case '3months':
          startDate = subMonths(now, 3);
          break;
        case '6months':
          startDate = subMonths(now, 6);
          break;
        case '1year':
          startDate = subMonths(now, 12);
          break;
        case 'custom':
          if (filter.startDate && filter.endDate) {
            filtered = filtered.filter(t => 
              isWithinInterval(new Date(t.date), {
                start: filter.startDate!,
                end: filter.endDate!
              })
            );
          }
          startDate = new Date(0);
          break;
        default:
          startDate = new Date(0);
      }
      
      if (filter.period !== 'custom') {
        filtered = filtered.filter(t => new Date(t.date) >= startDate);
      }
    }
    
    // Menu filter
    if (filter.menu) {
      filtered = filtered.filter(t => t.treatmentMenu === filter.menu);
    }
    
    // Staff filter
    if (filter.staff) {
      filtered = filtered.filter(t => t.staffName === filter.staff);
    }
    
    // Keyword search
    if (filter.keyword) {
      const keyword = filter.keyword.toLowerCase();
      filtered = filtered.filter(t => {
        const searchableText = [
          t.treatmentMenu,
          t.staffName,
          t.symptoms?.join(' '),
          t.physicalCondition,
          // Add any memo/notes fields when available
        ].filter(Boolean).join(' ').toLowerCase();
        
        return searchableText.includes(keyword);
      });
    }
    
    // Sort by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
    
    return filtered;
  }, [treatments, filter, sortOrder]);
  
  // Group treatments by year and month
  const groupedTreatments = useMemo(() => {
    const groups = new Map<string, TreatmentRecord[]>();
    
    filteredTreatments.forEach(treatment => {
      const date = treatment.date instanceof Date ? treatment.date : new Date(treatment.date);
      const key = format(date, 'yyyy年MM月', { locale: ja });
      
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(treatment);
    });
    
    return Array.from(groups.entries()).map(([key, records]) => ({
      key,
      records
    }));
  }, [filteredTreatments]);
  
  // Handler functions
  const handleFilterMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setFilterMenuAnchor(event.currentTarget);
  };
  
  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };
  
  const handlePeriodChange = (event: SelectChangeEvent) => {
    setFilter({ ...filter, period: event.target.value as TimelineFilter['period'] });
  };
  
  const handleMenuChange = (event: SelectChangeEvent) => {
    setFilter({ ...filter, menu: event.target.value });
  };
  
  const handleStaffChange = (event: SelectChangeEvent) => {
    setFilter({ ...filter, staff: event.target.value });
  };
  
  const handleKeywordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter({ ...filter, keyword: event.target.value });
  };
  
  const toggleGroupExpanded = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };
  
  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
  };
  
  const clearFilters = () => {
    setFilter({
      period: 'all',
      menu: '',
      staff: '',
      keyword: ''
    });
  };
  
  const activeFilterCount = [
    filter.period !== 'all',
    filter.menu !== '',
    filter.staff !== '',
    filter.keyword !== ''
  ].filter(Boolean).length;
  
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        {[...Array(3)].map((_, i) => (
          <Skeleton 
            key={i}
            variant="rectangular" 
            height={150} 
            sx={{ mb: 2, borderRadius: 2 }} 
          />
        ))}
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Filter Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            size="small"
            placeholder="キーワード検索..."
            value={filter.keyword}
            onChange={handleKeywordChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: 300 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>期間</InputLabel>
            <Select
              value={filter.period}
              label="期間"
              onChange={handlePeriodChange}
            >
              <MenuItem value="all">全期間</MenuItem>
              <MenuItem value="1month">直近1ヶ月</MenuItem>
              <MenuItem value="3months">直近3ヶ月</MenuItem>
              <MenuItem value="6months">直近6ヶ月</MenuItem>
              <MenuItem value="1year">直近1年</MenuItem>
            </Select>
          </FormControl>
          
          <IconButton
            onClick={handleFilterMenuOpen}
            color={activeFilterCount > 0 ? 'primary' : 'default'}
          >
            <FilterListIcon />
            {activeFilterCount > 0 && (
              <Chip
                label={activeFilterCount}
                size="small"
                color="primary"
                sx={{
                  position: 'absolute',
                  top: -4,
                  right: -4,
                  height: 20,
                  minWidth: 20,
                  '& .MuiChip-label': { px: 0.5 }
                }}
              />
            )}
          </IconButton>
          
          <Button
            size="small"
            onClick={toggleSortOrder}
            startIcon={sortOrder === 'desc' ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
          >
            {sortOrder === 'desc' ? '新しい順' : '古い順'}
          </Button>
        </Stack>
        
        {/* Active Filters */}
        {activeFilterCount > 0 && (
          <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              フィルター:
            </Typography>
            {filter.period !== 'all' && (
              <Chip
                size="small"
                label={filter.period === '1month' ? '直近1ヶ月' :
                       filter.period === '3months' ? '直近3ヶ月' :
                       filter.period === '6months' ? '直近6ヶ月' :
                       filter.period === '1year' ? '直近1年' : 'カスタム期間'}
                onDelete={() => setFilter({ ...filter, period: 'all' })}
              />
            )}
            {filter.menu && (
              <Chip
                size="small"
                label={filter.menu}
                onDelete={() => setFilter({ ...filter, menu: '' })}
              />
            )}
            {filter.staff && (
              <Chip
                size="small"
                label={filter.staff}
                onDelete={() => setFilter({ ...filter, staff: '' })}
              />
            )}
            <Button size="small" onClick={clearFilters}>
              すべてクリア
            </Button>
          </Stack>
        )}
      </Paper>
      
      {/* Timeline Content */}
      {filteredTreatments.length === 0 ? (
        <TimelineEmpty hasFilters={activeFilterCount > 0} customerId={customerId} />
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {filteredTreatments.length}件の施術記録
          </Typography>
          
          {groupedTreatments.map(({ key, records }) => {
            const isExpanded = expandedGroups.has(key) || expandedGroups.size === 0;
            
            return (
              <Box key={key} sx={{ mb: 3 }}>
                <Button
                  fullWidth
                  sx={{
                    justifyContent: 'space-between',
                    mb: 1,
                    color: 'text.primary',
                    textTransform: 'none',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => toggleGroupExpanded(key)}
                >
                  <Typography variant="subtitle1" fontWeight="medium">
                    {key}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={`${records.length}件`} size="small" />
                    {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </Stack>
                </Button>
                
                {isExpanded && (
                  <Box>
                    {records.length > 5 ? (
                      <List
                        height={Math.min(records.length * 180, 600)}
                        itemCount={records.length}
                        itemSize={180}
                        width="100%"
                        children={((props: { index: number; style: React.CSSProperties }) => (
                          <Box style={props.style} sx={{ pb: 2 }}>
                            <TimelineCard
                              treatment={records[props.index]}
                              onClick={() => onSelectRecord?.(records[props.index])}
                            />
                          </Box>
                        )) as any}
                      />
                    ) : (
                      <Stack spacing={2}>
                        {records.map((treatment) => (
                          <TimelineCard
                            key={treatment.id}
                            treatment={treatment}
                            onClick={() => onSelectRecord?.(treatment)}
                          />
                        ))}
                      </Stack>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}
      
      {/* Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={handleFilterMenuClose}
      >
        <Box sx={{ p: 2, minWidth: 280 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            詳細フィルター
          </Typography>
          
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>施術メニュー</InputLabel>
            <Select
              value={filter.menu}
              label="施術メニュー"
              onChange={handleMenuChange}
            >
              <MenuItem value="">すべて</MenuItem>
              {menus.map(menu => (
                <MenuItem key={menu} value={menu}>{menu}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>担当スタッフ</InputLabel>
            <Select
              value={filter.staff}
              label="担当スタッフ"
              onChange={handleStaffChange}
            >
              <MenuItem value="">すべて</MenuItem>
              {staffList.map(staff => (
                <MenuItem key={staff} value={staff}>{staff}</MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button
            fullWidth
            variant="contained"
            size="small"
            onClick={handleFilterMenuClose}
          >
            適用
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default TimelineView;